/**
 * The realm schema's migrations, read from `schema/migrations` at the repository root -
 * the same files the desktop edition bundles (`persistence.RealmMigrations`), so the two
 * editions cannot drift. See `schema/README.md` for the rules.
 */
export interface Migration {
  readonly version: number;
  readonly description: string;
  readonly sql: string;
}

const files = import.meta.glob<string>('../../../../schema/migrations/*.sql', {
  query: '?raw',
  import: 'default',
  eager: true,
});

/** Splits off the `-- description` first line, as the desktop's `RealmMigrations.parse` does. */
export function parseMigration(version: number, text: string): Migration {
  const normalized = text.replace(/\r\n/g, '\n');
  const newline = normalized.indexOf('\n');
  const firstLine = newline < 0 ? normalized : normalized.slice(0, newline);
  if (!firstLine.startsWith('-- ')) {
    throw new Error(`Migration ${version} must start with a '-- description' line`);
  }
  return {
    version,
    description: firstLine.slice(3).trim(),
    sql: newline < 0 ? '' : normalized.slice(newline + 1),
  };
}

/** Orders migration files by their `NNNN.sql` number and checks they run 1, 2, 3... with no gaps. */
export function loadMigrations(byPath: Record<string, string>): Migration[] {
  const numbered = Object.entries(byPath).map(([path, text]) => {
    const match = /(\d{4})\.sql$/.exec(path);
    if (!match?.[1]) {
      throw new Error(`Unexpected migration file name: ${path}`);
    }
    return parseMigration(Number(match[1]), text);
  });
  numbered.sort((a, b) => a.version - b.version);
  numbered.forEach((migration, index) => {
    if (migration.version !== index + 1) {
      throw new Error(`Realm migrations must be numbered 1..n without gaps; found ${migration.version} at ${index + 1}`);
    }
  });
  if (numbered.length === 0) {
    throw new Error('No realm migrations found in schema/migrations');
  }
  return numbered;
}

export const REALM_MIGRATIONS: readonly Migration[] = Object.freeze(loadMigrations(files));

/** The newest realm format this build understands; a realm file stamped higher is refused. */
export const LATEST_REALM_VERSION: number = REALM_MIGRATIONS[REALM_MIGRATIONS.length - 1]!.version;
